import { useEffect, useState, type FormEvent } from "react";
import type {
  Caja,
  CategoriaEconomica,
  Moneda,
  MovimientoEconomico,
  SaldoPorMoneda,
  SubcategoriaEconomica,
  TipoMovimientoEconomico,
} from "../../../shared/types";
import { AlternativaBinaria } from "../../components/AlternativaBinaria";
import { Boton } from "../../components/Boton";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { Campo, Input, Select } from "../../components/Formulario";
import { Modal } from "../../components/Modal";
import { SelectorCategoriaEconomica } from "../../components/SelectorCategoriaEconomica";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { TarjetaIndicador } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { NATURALEZA_CATEGORIA } from "../../lib/economia";
import { PresupuestadorHonorarios } from "./PresupuestadorHonorarios";

interface ExpedienteBasico {
  id: string;
  numero_expediente: string;
  cliente_nombre: string;
}

const TIPOS: TipoMovimientoEconomico[] = [
  "Capital del Cliente",
  "Honorarios Pactados",
  "Honorarios Regulados",
  "Honorarios Cobrados",
  "Gasto Adelantado",
  "Gasto Recuperado",
  "Pago Recibido",
  "Pago al Cliente",
  "Pago a Profesional",
  "Aporte",
  "Retención",
  "Otro Ingreso",
  "Otro Egreso",
];

// "Pago Recibido" queda fuera del selector de carga manual: no está
// vinculado a ningún saldo (ver lib/economia.ts, NATURALEZA_CLIENTE/
// NATURALEZA_ESTUDIO) ni lo genera ninguna ruta del backend — es el tipo
// más intuitivo para "el cliente pagó", lo que lo hacía una trampa real
// (auditoría de integridad). Sigue existiendo en el enum/CHECK de la base
// (no se rompe ningún registro histórico) y en el filtro de abajo, para
// poder seguir viendo/filtrando movimientos ya cargados con ese tipo.
const TIPOS_SELECCIONABLES = TIPOS.filter((t) => t !== "Pago Recibido");

const MONEDAS: Moneda[] = ["ARS", "USD"];

const VALORES_INICIALES = {
  tipo: "Capital del Cliente" as TipoMovimientoEconomico,
  concepto: "",
  fecha: new Date().toISOString().substring(0, 10),
  importe: "",
  moneda: "ARS" as Moneda,
  caja_id: "",
  categoria_id: "",
  subcategoria_id: "",
  expediente_id: "",
};

const FILTROS_INICIALES = {
  tipo: "",
  categoria_id: "",
  subcategoria_id: "",
  moneda: "",
  caja_id: "",
  fecha_desde: "",
  fecha_hasta: "",
};

export function EconomiaPage() {
  const { mostrar } = useToast();
  const [movimientos, setMovimientos] = useState<MovimientoEconomico[]>([]);
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [categoriasFiltro, setCategoriasFiltro] = useState<CategoriaEconomica[]>([]);
  const [subcategoriasFiltro, setSubcategoriasFiltro] = useState<SubcategoriaEconomica[]>([]);
  const [saldosPorMoneda, setSaldosPorMoneda] = useState<SaldoPorMoneda[]>([]);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalCajaAbierto, setModalCajaAbierto] = useState(false);
  const [presupuestadorAbierto, setPresupuestadorAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [guardandoCaja, setGuardandoCaja] = useState(false);
  const [valores, setValores] = useState(VALORES_INICIALES);
  const [perteneceExpediente, setPerteneceExpediente] = useState(false);
  const [expedientesDisponibles, setExpedientesDisponibles] = useState<ExpedienteBasico[]>([]);
  const [soloGeneral, setSoloGeneral] = useState(false);
  const [nuevaCaja, setNuevaCaja] = useState({ nombre: "", moneda: "ARS" as Moneda });

  function cargar() {
    setCargando(true);
    const params = new URLSearchParams();
    if (filtros.tipo) params.set("tipo", filtros.tipo);
    if (filtros.categoria_id) params.set("categoria_id", filtros.categoria_id);
    if (filtros.subcategoria_id) params.set("subcategoria_id", filtros.subcategoria_id);
    if (filtros.moneda) params.set("moneda", filtros.moneda);
    if (filtros.caja_id) params.set("caja_id", filtros.caja_id);
    if (filtros.fecha_desde) params.set("fecha_desde", filtros.fecha_desde);
    if (filtros.fecha_hasta) params.set("fecha_hasta", filtros.fecha_hasta);
    const query = params.toString();
    api
      .get<{ items: MovimientoEconomico[]; saldosPorMoneda: SaldoPorMoneda[] }>(
        `/economia${query ? `?${query}` : ""}`,
      )
      .then((d) => {
        setMovimientos(d.items);
        setSaldosPorMoneda(d.saldosPorMoneda);
      })
      .catch(() => mostrar("No se pudieron cargar los movimientos.", "error"))
      .finally(() => setCargando(false));
  }

  function cargarCajas() {
    api
      .get<{ items: Caja[] }>("/cajas")
      .then((d) => setCajas(d.items))
      .catch(() => undefined);
  }

  // Sólo se usa para el selector "¿Pertenece a un expediente?" del modal —
  // un rol con economia.ver pero sin acceso a Expedientes simplemente ve el
  // selector vacío, sin toast de error (mismo criterio que otros lookups
  // opcionales de este bloque).
  function cargarExpedientes() {
    api
      .get<{ items: ExpedienteBasico[] }>("/expedientes")
      .then((d) => setExpedientesDisponibles(d.items))
      .catch(() => undefined);
  }

  useEffect(cargar, [filtros]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(cargarCajas, []);
  useEffect(cargarExpedientes, []);

  // Filtro de Categoría: cuando se elige un Tipo con naturaleza mapeada, se
  // acota a esa naturaleza; sin Tipo (o uno sin naturaleza), se muestran
  // todas las categorías de ambas naturalezas para no bloquear el filtro.
  useEffect(() => {
    const naturaleza = filtros.tipo ? NATURALEZA_CATEGORIA[filtros.tipo as TipoMovimientoEconomico] : undefined;
    api
      .get<{ items: CategoriaEconomica[] }>(`/economia-catalogos/categorias${naturaleza ? `?naturaleza=${naturaleza}` : ""}`)
      .then((d) => setCategoriasFiltro(d.items))
      .catch(() => setCategoriasFiltro([]));
  }, [filtros.tipo]);

  useEffect(() => {
    if (!filtros.categoria_id) {
      setSubcategoriasFiltro([]);
      return;
    }
    api
      .get<{ items: SubcategoriaEconomica[] }>(`/economia-catalogos/subcategorias?categoria_id=${filtros.categoria_id}`)
      .then((d) => setSubcategoriasFiltro(d.items))
      .catch(() => setSubcategoriasFiltro([]));
  }, [filtros.categoria_id]);

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post("/economia", {
        ...valores,
        importe: Number(valores.importe),
        caja_id: valores.caja_id || null,
        categoria_id: valores.categoria_id || null,
        subcategoria_id: valores.subcategoria_id || null,
        expediente_id: perteneceExpediente ? valores.expediente_id || null : null,
      });
      mostrar("Movimiento registrado.", "exito");
      setModalAbierto(false);
      setValores(VALORES_INICIALES);
      setPerteneceExpediente(false);
      cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo registrar el movimiento.", "error");
    } finally {
      setGuardando(false);
    }
  }

  async function manejarEnvioCaja(e: FormEvent) {
    e.preventDefault();
    setGuardandoCaja(true);
    try {
      await api.post("/cajas", nuevaCaja);
      mostrar("Caja creada.", "exito");
      setModalCajaAbierto(false);
      setNuevaCaja({ nombre: "", moneda: "ARS" });
      cargarCajas();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo crear la caja.", "error");
    } finally {
      setGuardandoCaja(false);
    }
  }

  const columnas: ColumnaTabla<MovimientoEconomico>[] = [
    { encabezado: "Fecha", render: (m) => m.fecha },
    { encabezado: "Tipo", render: (m) => m.tipo },
    {
      encabezado: "Unidad",
      render: (m) =>
        m.area_practica_nombre ? (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: m.area_practica_color ?? undefined }}
            />
            {m.area_practica_nombre}
          </span>
        ) : (
          <span className="rounded-full bg-fondo px-2 py-0.5 text-xs font-medium text-texto-secundario">
            General
          </span>
        ),
    },
    { encabezado: "Categoría", render: (m) => m.categoria_nombre ?? "—" },
    { encabezado: "Subcategoría", render: (m) => m.subcategoria_nombre ?? "—" },
    { encabezado: "Concepto", render: (m) => m.concepto },
    { encabezado: "Profesional", render: (m) => m.profesional_beneficiario_nombre ?? "—" },
    {
      encabezado: "Caja",
      render: (m) => cajas.find((cj) => cj.id === m.caja_id)?.nombre ?? "—",
    },
    {
      encabezado: "Importe",
      render: (m) => (
        <span className="text-texto">
          ${m.importe.toLocaleString("es-AR")} {m.moneda}
        </span>
      ),
    },
  ];

  return (
    <div>
      <EncabezadoPagina
        titulo="Economía"
        descripcion="Ingresos, egresos y honorarios del estudio"
        acciones={
          <>
            <Boton variante="secundario" onClick={() => setModalCajaAbierto(true)}>
              + Nueva Caja
            </Boton>
            <Boton variante="secundario" onClick={() => setPresupuestadorAbierto(true)}>
              Presupuestador de Honorarios
            </Boton>
            <Boton onClick={() => setModalAbierto(true)}>+ Nuevo Movimiento</Boton>
          </>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <Select
          value={filtros.tipo}
          onChange={(e) => setFiltros((f) => ({ ...f, tipo: e.target.value, categoria_id: "", subcategoria_id: "" }))}
        >
          <option value="">Todos los tipos</option>
          {TIPOS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select
          value={filtros.categoria_id}
          onChange={(e) => setFiltros((f) => ({ ...f, categoria_id: e.target.value, subcategoria_id: "" }))}
        >
          <option value="">Todas las categorías</option>
          {categoriasFiltro.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.nombre}
            </option>
          ))}
        </Select>
        <Select
          value={filtros.subcategoria_id}
          onChange={(e) => setFiltros((f) => ({ ...f, subcategoria_id: e.target.value }))}
          disabled={subcategoriasFiltro.length === 0}
        >
          <option value="">Todas las subcategorías</option>
          {subcategoriasFiltro.map((sub) => (
            <option key={sub.id} value={sub.id}>
              {sub.nombre}
            </option>
          ))}
        </Select>
        <Select value={filtros.moneda} onChange={(e) => setFiltros((f) => ({ ...f, moneda: e.target.value }))}>
          <option value="">Todas las monedas</option>
          {MONEDAS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
        <Select value={filtros.caja_id} onChange={(e) => setFiltros((f) => ({ ...f, caja_id: e.target.value }))}>
          <option value="">Todas las cajas</option>
          {cajas.map((cj) => (
            <option key={cj.id} value={cj.id}>
              {cj.nombre} ({cj.moneda})
            </option>
          ))}
        </Select>
        <Input
          type="date"
          value={filtros.fecha_desde}
          onChange={(e) => setFiltros((f) => ({ ...f, fecha_desde: e.target.value }))}
          placeholder="Desde"
        />
        <Input
          type="date"
          value={filtros.fecha_hasta}
          onChange={(e) => setFiltros((f) => ({ ...f, fecha_hasta: e.target.value }))}
          placeholder="Hasta"
        />
      </div>

      {/* Un bloque de tarjetas por moneda presente — ARS y USD nunca se
          combinan en un único "Saldo Cliente"/"Saldo Estudio" (ver
          calcularSaldos en functions/_app/lib/economia.ts). Sin ningún
          movimiento todavía, no hay ninguna moneda que mostrar. */}
      <div className="mb-6 space-y-3">
        {saldosPorMoneda.map((s) => (
          <div key={s.moneda} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TarjetaIndicador
              etiqueta={`Saldo Cliente (${s.moneda})`}
              valor={`$${s.saldoCliente.toLocaleString("es-AR")}`}
              tono={s.saldoCliente >= 0 ? "exito" : "alerta"}
            />
            <TarjetaIndicador
              etiqueta={`Saldo Estudio (${s.moneda})`}
              valor={`$${s.saldoEstudio.toLocaleString("es-AR")}`}
              tono={s.saldoEstudio >= 0 ? "exito" : "alerta"}
            />
          </div>
        ))}
        <TarjetaIndicador etiqueta="Movimientos registrados" valor={movimientos.length} />
      </div>

      <div className="mb-3">
        <button
          type="button"
          aria-pressed={soloGeneral}
          onClick={() => setSoloGeneral((v) => !v)}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            soloGeneral
              ? "border-principal bg-principal text-superficie"
              : "border-borde bg-superficie text-texto-secundario hover:bg-fondo"
          }`}
        >
          Solo Gasto General
        </button>
      </div>

      <Tabla
        columnas={columnas}
        filas={soloGeneral ? movimientos.filter((m) => !m.area_practica_nombre) : movimientos}
        claveFila={(m) => m.id}
        cargando={cargando}
        tituloVacio="No hay movimientos"
        descripcionVacio="Registrá el primer movimiento del estudio."
      />

      <Modal
        titulo="Nuevo movimiento"
        abierto={modalAbierto}
        onCerrar={() => {
          setModalAbierto(false);
          setPerteneceExpediente(false);
        }}
      >
        <form onSubmit={manejarEnvio} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Tipo" obligatorio>
              <Select
                value={valores.tipo}
                onChange={(e) =>
                  setValores((v) => ({
                    ...v,
                    tipo: e.target.value as TipoMovimientoEconomico,
                    categoria_id: "",
                    subcategoria_id: "",
                  }))
                }
              >
                {TIPOS_SELECCIONABLES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
              {valores.tipo === "Retención" && (
                <p className="mt-1 text-xs text-texto-secundario">
                  Este tipo es informativo: no se suma al Saldo Cliente ni al Saldo Estudio.
                </p>
              )}
            </Campo>
            <Campo etiqueta="Fecha" obligatorio>
              <Input
                type="date"
                required
                value={valores.fecha}
                onChange={(e) => setValores((v) => ({ ...v, fecha: e.target.value }))}
              />
            </Campo>
          </div>

          <Campo etiqueta="¿Pertenece a un expediente?">
            <AlternativaBinaria
              valor={perteneceExpediente}
              onCambiar={(v) => {
                setPerteneceExpediente(v);
                if (!v) setValores((val) => ({ ...val, expediente_id: "" }));
              }}
              etiquetaNo="No (Gasto General)"
            />
          </Campo>
          {perteneceExpediente && (
            <Campo etiqueta="Expediente" obligatorio>
              <Select
                required
                value={valores.expediente_id}
                onChange={(e) => setValores((v) => ({ ...v, expediente_id: e.target.value }))}
              >
                <option value="">Seleccionar...</option>
                {expedientesDisponibles.map((exp) => (
                  <option key={exp.id} value={exp.id}>
                    {exp.numero_expediente} — {exp.cliente_nombre}
                  </option>
                ))}
              </Select>
            </Campo>
          )}

          <SelectorCategoriaEconomica
            naturaleza={NATURALEZA_CATEGORIA[valores.tipo] ?? null}
            categoriaId={valores.categoria_id}
            subcategoriaId={valores.subcategoria_id}
            onCambiarCategoria={(id) => setValores((v) => ({ ...v, categoria_id: id }))}
            onCambiarSubcategoria={(id) => setValores((v) => ({ ...v, subcategoria_id: id }))}
          />

          <Campo etiqueta="Concepto" obligatorio>
            <Input
              required
              value={valores.concepto}
              onChange={(e) => setValores((v) => ({ ...v, concepto: e.target.value }))}
            />
          </Campo>
          <div className="grid grid-cols-3 gap-4">
            <Campo etiqueta="Importe" obligatorio>
              <Input
                type="number"
                min="0"
                step="0.01"
                required
                value={valores.importe}
                onChange={(e) => setValores((v) => ({ ...v, importe: e.target.value }))}
              />
            </Campo>
            <Campo etiqueta="Moneda" obligatorio>
              <Select
                value={valores.moneda}
                onChange={(e) => setValores((v) => ({ ...v, moneda: e.target.value as Moneda }))}
              >
                {MONEDAS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Caja">
              <Select value={valores.caja_id} onChange={(e) => setValores((v) => ({ ...v, caja_id: e.target.value }))}>
                <option value="">Sin caja</option>
                {cajas.map((cj) => (
                  <option key={cj.id} value={cj.id}>
                    {cj.nombre}
                  </option>
                ))}
              </Select>
            </Campo>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => {
                setModalAbierto(false);
                setPerteneceExpediente(false);
              }}
              disabled={guardando}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal titulo="Nueva caja" abierto={modalCajaAbierto} onCerrar={() => setModalCajaAbierto(false)}>
        <form onSubmit={manejarEnvioCaja} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={nuevaCaja.nombre}
              onChange={(e) => setNuevaCaja((v) => ({ ...v, nombre: e.target.value }))}
              placeholder="Caja Pesos / Caja Dólares"
            />
          </Campo>
          <Campo etiqueta="Moneda" obligatorio>
            <Select
              value={nuevaCaja.moneda}
              onChange={(e) => setNuevaCaja((v) => ({ ...v, moneda: e.target.value as Moneda }))}
            >
              {MONEDAS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalCajaAbierto(false)}
              disabled={guardandoCaja}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoCaja}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <PresupuestadorHonorarios
        abierto={presupuestadorAbierto}
        onCerrar={() => setPresupuestadorAbierto(false)}
        onCreado={cargar}
      />
    </div>
  );
}
